/**
 * Resolve which API origin a page should call.
 *
 * KNE is served under more than one registrable domain. The session cookie is
 * scoped to the domain that issued it, so a page must talk to the API on its
 * own domain or the browser will never send the cookie along. One build serves
 * every domain, so the choice has to happen at request time rather than being
 * baked in by NEXT_PUBLIC_API_URL.
 *
 * NEXT_PUBLIC_API_URL_BY_HOST maps a host to its API origin. Anything not
 * listed -- previews, localhost, vercel.app URLs -- falls back to
 * NEXT_PUBLIC_API_URL, which is what every page used before.
 */

export interface ApiBaseEnvironment {
  fallback?: string;
  host?: string | null;
  hostMap?: string;
}

const normaliseHost = (host: string): string =>
  host.split(':')[0].trim().toLowerCase().replace(/\.$/, '');

const parseHostMap = (raw?: string): Record<string, string> => {
  if (!raw) return {};

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return {};
  }

  return Object.entries(parsed as Record<string, unknown>).reduce<Record<string, string>>(
    (map, [host, origin]) => {
      if (typeof origin === 'string' && origin) {
        map[normaliseHost(host)] = origin;
      }
      return map;
    },
    {},
  );
};

export function resolveApiBase(environment: ApiBaseEnvironment): string {
  const fallback = environment.fallback ?? '';

  if (!environment.host) return fallback;

  return parseHostMap(environment.hostMap)[normaliseHost(environment.host)] ?? fallback;
}

const isServer = typeof window === 'undefined';

const currentHost = async (): Promise<string | null> => {
  if (!isServer) return window.location.host;

  try {
    const { headers } = await import('next/headers');
    const store = await headers();
    return store.get('host');
  } catch {
    // No request to read a host from, as during static generation.
    return null;
  }
};

export const getApiBase = async (): Promise<string> =>
  resolveApiBase({
    fallback: process.env.NEXT_PUBLIC_API_URL,
    host: await currentHost(),
    hostMap: process.env.NEXT_PUBLIC_API_URL_BY_HOST,
  });
